import type { RoundProvider } from "./RoundProvider";
import type { PlaySession } from "./PlaySession";
import { retryAfterMs } from "./retryAfter";
import { SpinTransportError, type SpinTransportErrorKind } from "./SpinTransportError";

import type { SpinRequest } from "../contracts/SpinRequest";
import type { SpinResponse, SpinResult, RejectedSpinResponse } from "../contracts/SpinResponse";

import type {
  PresentationCompleteRequest,
  PresentationCompleteResponse,
} from "../contracts/PresentationCompleteRequest";

import type {
  GambleRequest,
  GambleResponse,
  GambleCompleteRequest,
  GambleCompleteResponse,
} from "../contracts/GambleContracts";

/**
 * BackendRoundProvider is a thin transport wrapper.
 *
 * - It does NOT perform client-side math.
 * - It does NOT interpret game logic.
 * - It only sends requests and returns server DTOs.
 *
 * Non-2xx responses that carry a contract-valid RejectedSpinResponse body
 * (e.g. 400 schema-validation, 402 insufficient-funds, 403 limit-reached) are returned as
 * rejections, not thrown — the engine distinguishes via `responseMetadata`. A 5xx with such a
 * body is a rejection too (decision 79): the server answered, explained, and placed no round — a
 * wallet timeout refunded inline, the wallet unavailable, rate limited. Only transport failures
 * (network, a 401, a 5xx without a rejection body, non-contract bodies) throw. A spin is sent once:
 * one whose reply never came may have landed, so it stays a transport failure.
 *
 * With the boot's play session every request carries its bearer (decision 81, the backend's ADR 0072), and every spin
 * and settlement its next number, one counter for both (decision 80, ADR 0070). A request asked for again after it got
 * no answer (the engine's one settlement retry, decision 17) keeps its number, so the backend answers it with the first
 * one's outcome; a request the backend is still running (409 `request_in_progress`, which only such a repeat can meet)
 * goes again after a pause, three times at most; a numbered request the backend limited (429, refused before its number
 * is claimed) goes again once when the wait it names is short. A 5xx whose body is not the backend's own is no answer
 * from it (`network`).
 */
export interface BackendRoundProviderConfiguration {
  /**
   * Example: "https://api.example.com"
   * Trailing slash is allowed.
   */
  baseUrl: string;

  /**
   * Example: "/bet" or "/spin"
   * Must return SpinResponse (on 2xx) or RejectedSpinResponse (on 4xx).
   */
  roundRequestPath: string;

  /**
   * Example: "/presentation-complete"
   * Must return PresentationCompleteResponse.
   */
  presentationCompletePath: string;

  /**
   * Example: "/gamble"
   * Must return GambleResponse. Server-authoritative gamble decision.
   */
  gambleRequestPath: string;

  /**
   * Example: "/gamble/complete"
   * Must return GambleCompleteResponse. Sent after the gamble flow ends.
   */
  gambleCompletePath: string;

  /**
   * Optional headers (auth, session, etc.).
   * Called per request.
   */
  getRequestHeaders?: () => Record<string, string>;

  /**
   * Per-request timeout in milliseconds. Aborts the fetch, body included, via `AbortSignal.timeout`
   * and surfaces as a network-error throw so the spin coordinator's error
   * path handles it the same way as any other transport failure.
   * Default 10_000ms.
   */
  requestTimeoutMs?: number;

  /**
   * The play session the boot started (decisions 80, 81): its bearer on every request, the gamble's included, and its
   * numbers on the spins and settlements; and the wall-clock pause before a request the backend asked to wait for goes
   * again. Absent (a backend without sessions answered 404, a replay, offline): no bearer and no numbers.
   */
  session?: {
    playSession: PlaySession;
    wait: (ms: number) => Promise<void>;
  };
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

/** The backend's rule for 409 `request_in_progress`: wait a second or two and send it again, a few times, then reload. */
const IN_PROGRESS_WAIT_MS = 1_500;
const IN_PROGRESS_RESENDS = 3;

/**
 * The longest wait a 429 may name for its numbered request to go again by itself (decision 81): the player is looking
 * at a spin in flight. A longer wait, or none named, ends the request with the 429, and the player is told nothing was
 * bet.
 */
const RATE_LIMIT_RESEND_MAX_WAIT_MS = 5_000;

/** A reply as it came: the response and its body, read under the request's deadline. */
interface Reply {
  response: Response;
  rawBody: string;
}

export class BackendRoundProvider implements RoundProvider {
  private readonly configuration: BackendRoundProviderConfiguration;

  public constructor(configuration: BackendRoundProviderConfiguration) {
    this.configuration = configuration;
  }

  public async requestRoundSnapshot(spinRequest: SpinRequest): Promise<SpinResult> {
    const url = this.buildUrl(this.configuration.roundRequestPath);
    const { response, rawBody } = await this.exchange(url, spinRequest, true);

    if (response.status >= 500) {
      // A refusal the server explains is a verdict like a 4xx one (decision 79); anything else at 5xx is the server
      // failing, or no answer from it at all (decision 80).
      const explained = parseRejectedSpinResponse(rawBody);
      if (explained) return explained;
      throw failedReply(url, response, rawBody);
    }
    if (response.status === 401) throw failedReply(url, response, rawBody);

    let parsedBody: unknown;
    try {
      parsedBody = rawBody.length > 0 ? JSON.parse(rawBody) : undefined;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new SpinTransportError(
        "protocol",
        `BackendRoundProvider: failed to parse response body from ${url} (status=${response.status}): ${message}. Body="${rawBody}"`,
      );
    }

    if (response.ok) {
      if (!isShapedSpinResponse(parsedBody)) {
        throw new SpinTransportError(
          "protocol",
          `BackendRoundProvider: 2xx response from ${url} is not a SpinResponse (missing roundSnapshot). Body=${rawBody}`,
        );
      }
      return parsedBody;
    }

    if (isShapedRejectedSpinResponse(parsedBody)) {
      return parsedBody;
    }

    throw new SpinTransportError(
      "protocol",
      `BackendRoundProvider: non-2xx response from ${url} (status=${response.status} ${response.statusText}) is not a RejectedSpinResponse. Body=${rawBody}`,
    );
  }

  public async acknowledgePresentationComplete(
    request: PresentationCompleteRequest
  ): Promise<PresentationCompleteResponse> {
    const url = this.buildUrl(this.configuration.presentationCompletePath);
    return await this.postJson<PresentationCompleteResponse>(url, request, isShapedSettlementResponse, true);
  }

  public async requestGamble(request: GambleRequest): Promise<GambleResponse> {
    const url = this.buildUrl(this.configuration.gambleRequestPath);
    const { response, rawBody } = await this.exchange(url, request, false);

    if (!response.ok) throw failedReply(url, response, rawBody);

    let parsedBody: unknown;
    try {
      parsedBody = rawBody.length > 0 ? JSON.parse(rawBody) : undefined;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new SpinTransportError(
        "protocol",
        `BackendRoundProvider: failed to parse response body from ${url}: ${message}. Body="${rawBody}"`,
      );
    }

    if (!isShapedGambleResponse(parsedBody)) {
      throw new SpinTransportError(
        "protocol",
        `BackendRoundProvider: 2xx response from ${url} is not a GambleResponse. Body=${rawBody}`,
      );
    }

    return parsedBody;
  }

  public async acknowledgeGambleComplete(
    request: GambleCompleteRequest
  ): Promise<GambleCompleteResponse> {
    const url = this.buildUrl(this.configuration.gambleCompletePath);
    return await this.postJson<GambleCompleteResponse>(url, request, isShapedSettlementResponse, false);
  }

  private buildUrl(path: string): string {
    const baseUrl = this.configuration.baseUrl.replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return baseUrl + normalizedPath;
  }

  /** The session's bearer goes last (decision 81): a custom header cannot replace it. */
  private buildHeaders(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      ...(this.configuration.getRequestHeaders?.() ?? {}),
      ...(this.configuration.session?.playSession.authorizationHeader() ?? {}),
    };
  }

  /**
   * The settlement and the gamble-complete. The body is read under the request's deadline like the
   * other two calls: a settlement whose body never arrives used to hang with the win on the reels
   * and no way back, where a timeout instead leaves the round pending for the next boot's restore
   * offer — the recovery decision 17 already describes (user, 2026-09-22). A 2xx is checked by
   * isShaped before it is returned, never cast: the balance these replies carry is the one the shell
   * puts on the readout (review 2026-09-27 #3).
   */
  private async postJson<TResponse>(
    url: string,
    payload: object,
    isShaped: (value: unknown) => value is TResponse,
    numbered: boolean,
  ): Promise<TResponse> {
    const { response, rawBody } = await this.exchange(url, payload, numbered);

    if (!response.ok) throw failedReply(url, response, rawBody);

    // Parsed here rather than through `response.json()`, so a malformed body is a protocol error
    // with the body in the message — the same shape the other two calls already produce — instead
    // of a bare SyntaxError the shell would classify as "unexpected".
    let parsedBody: unknown;
    try {
      parsedBody = JSON.parse(rawBody);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new SpinTransportError(
        "protocol",
        `BackendRoundProvider: failed to parse response body from ${url} (status=${response.status}): ${message}. Body="${rawBody}"`,
      );
    }

    if (!isShaped(parsedBody)) {
      throw new SpinTransportError(
        "protocol",
        `BackendRoundProvider: 2xx response from ${url} is not a settlement reply (roundIdentifier, accountBalanceSnapshot.balanceAmount in whole minor units). Body=${rawBody}`,
      );
    }
    return parsedBody;
  }

  /**
   * One request and its reply. Numbered (a spin or a settlement, with a play session; decisions 80, 81) it carries its
   * number, the bearer naming the session. While the backend answers 409 `request_in_progress` it goes again, with the
   * same number and body, after a pause: three times at most, then it fails as `server` and the shell reloads. A 429
   * came before the backend claimed the number (the backend, 2026-10-20), so nothing was done: the request goes again
   * once after the wait the backend names when that is short, and otherwise ends with the 429, its number left to the
   * next request.
   */
  private async exchange(url: string, payload: object, numbered: boolean): Promise<Reply> {
    const session = numbered ? this.configuration.session : undefined;
    if (!session) return await this.send(url, JSON.stringify(payload));

    const { playSession, wait } = session;
    const sequenceNumber = playSession.numberFor(`${url} ${JSON.stringify(payload)}`);
    // No message here or below carries a request body or a header.
    const body = JSON.stringify({ ...payload, sequenceNumber });
    for (let attempt = 0; ; attempt += 1) {
      const reply = await this.send(url, body);
      const { status } = reply.response;
      if (status === 409 && statusMessageOf(reply.rawBody) === "request_in_progress") {
        if (attempt === IN_PROGRESS_RESENDS) {
          throw new SpinTransportError("server", `BackendRoundProvider: request ${sequenceNumber} to ${url} was still in progress after ${IN_PROGRESS_RESENDS} resends`, "request_in_progress");
        }
        await wait(IN_PROGRESS_WAIT_MS);
        continue;
      }
      if (status === 429) {
        const waitMs = retryAfterMs(reply.response, reply.rawBody);
        if (attempt === 0 && waitMs !== null && waitMs <= RATE_LIMIT_RESEND_MAX_WAIT_MS) {
          await wait(waitMs);
          continue;
        }
        playSession.refusedBeforeClaim(sequenceNumber);
        return reply;
      }
      if (!isLostReply(reply)) playSession.answered(sequenceNumber);
      return reply;
    }
  }

  /** The POST and its body, both under the request's deadline: a failure to get either is no answer (`network`). */
  private async send(url: string, body: string): Promise<Reply> {
    try {
      const response = await this.fetchWithTimeout(url, { method: "POST", headers: this.buildHeaders(), body });
      // Read under the same deadline: a body that never arrives is the same transport failure as a
      // response that never arrives, and must reach the coordinator as one.
      return { response, rawBody: await response.text() };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new SpinTransportError(
        "network",
        `BackendRoundProvider: network error calling ${url}: ${message}`,
      );
    }
  }

  /**
   * The request's deadline, covering the HEADERS AND THE BODY.
   *
   * `fetch` resolves as soon as the response headers arrive, so an arm-then-clear timer (what this
   * used to be) disarms exactly when the interesting half of the exchange begins: a connection that
   * flushed headers and then stalled its body was unbounded, and on the spin path that left the bet
   * locked, the loading overlay up and the coordinator in `requesting` with no way out. An
   * `AbortSignal.timeout` stays armed for its whole duration and errors the body stream too, so
   * every caller must read the body INSIDE the try that maps failures to a network error.
   */
  private async fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
    const timeoutMs = this.configuration.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    return await fetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
  }
}

/**
 * A non-2xx reply that is no rejection, as the failure it is, the backend's code with it: a 5xx is the backend's own
 * error, or no answer from it when the body is not the backend's (decision 80); a 429 is the backend asking to wait,
 * `server` like an outage (decision 81); a 401 is the session's end; anything else is protocol.
 */
function failedReply(url: string, response: Response, rawBody: string): SpinTransportError {
  const statusMessage = statusMessageOf(rawBody);
  const { status } = response;
  const kind: SpinTransportErrorKind =
    status >= 500 ? (isLostReply({ response, rawBody }) ? "network" : "server") : status === 429 ? "server" : status === 401 ? "session" : "protocol";
  return new SpinTransportError(
    kind,
    `BackendRoundProvider: ${url} returned ${response.status} ${response.statusText}${statusMessage ? ` (${statusMessage})` : ""}. Body=${rawBody}`,
    statusMessage,
  );
}

/**
 * A 5xx whose body is not the backend's own error — a proxy's or the edge's page — is no answer from the backend: the
 * request may never have reached it, or its answer was lost (the backend, 2026-10-18; decision 80).
 */
function isLostReply(reply: Reply): boolean {
  return reply.response.status >= 500 && !isBackendErrorBody(reply.rawBody);
}

/** The backend's error bodies are JSON with `responseMetadata` (a refusal) or a `correlationId` (its generic error). */
function isBackendErrorBody(rawBody: string): boolean {
  const parsed = parseJsonOrNull(rawBody);
  return isRecord(parsed) && (isRecord(parsed.responseMetadata) || typeof parsed.correlationId === "string");
}

/** The backend's code on an error reply (`responseMetadata.statusMessage`), when it named one. */
function statusMessageOf(rawBody: string): string | undefined {
  const parsed = parseJsonOrNull(rawBody);
  if (!isRecord(parsed) || !isRecord(parsed.responseMetadata)) return undefined;
  const statusMessage = parsed.responseMetadata.statusMessage;
  return typeof statusMessage === "string" && statusMessage.length > 0 ? statusMessage : undefined;
}

function parseJsonOrNull(rawBody: string): unknown {
  if (rawBody.length === 0) return null;
  try {
    return JSON.parse(rawBody) as unknown;
  } catch {
    return null;
  }
}

function isShapedSpinResponse(value: unknown): value is SpinResponse {
  if (!isRecord(value)) return false;
  const snapshot = (value as { roundSnapshot?: unknown }).roundSnapshot;
  return isRecord(snapshot) && typeof (snapshot as { roundIdentifier?: unknown }).roundIdentifier === "string";
}

/** A body that parses to a RejectedSpinResponse, else null (an unparseable or generic error body is not one). */
function parseRejectedSpinResponse(rawBody: string): RejectedSpinResponse | null {
  const parsed = parseJsonOrNull(rawBody);
  return isShapedRejectedSpinResponse(parsed) ? parsed : null;
}

function isShapedRejectedSpinResponse(value: unknown): value is RejectedSpinResponse {
  if (!isRecord(value)) return false;
  const metadata = (value as { responseMetadata?: unknown }).responseMetadata;
  if (!isRecord(metadata)) return false;
  return (
    typeof (metadata as { statusCode?: unknown }).statusCode === "number" &&
    typeof (metadata as { statusMessage?: unknown }).statusMessage === "string"
  );
}

function isShapedGambleResponse(value: unknown): value is GambleResponse {
  if (!isRecord(value)) return false;
  const v = value as {
    roundIdentifier?: unknown;
    gambleOutcome?: unknown;
    possibleClientActions?: unknown;
    previousWinAmountMinor?: unknown;
    newWinAmountMinor?: unknown;
  };
  return (
    typeof v.roundIdentifier === "string" &&
    (v.gambleOutcome === "win" || v.gambleOutcome === "lose") &&
    Array.isArray(v.possibleClientActions) &&
    typeof v.previousWinAmountMinor === "string" &&
    typeof v.newWinAmountMinor === "string"
  );
}

/**
 * The settlement's and the gamble-complete's reply. The platform backend requires the round id and the balance
 * snapshot on every 200 of both, idempotent repeats included: its reply schemas are parsed before a reply leaves, and
 * a terminated round is a 404, never a 200. A 200 without them breaks the contract, so it is `protocol`, not a
 * settled win the readout never shows (review 2026-09-27 #3). The balance must be whole minor units, as
 * SpinResponseValidator requires of the spin reply's (decision 22). The gamble STEP reply's snapshot is optional
 * on the backend, so isShapedGambleResponse asks for none.
 */
function isShapedSettlementResponse(value: unknown): value is PresentationCompleteResponse & GambleCompleteResponse {
  if (!isRecord(value)) return false;
  const snapshot = (value as { accountBalanceSnapshot?: unknown }).accountBalanceSnapshot;
  if (!isRecord(snapshot)) return false;
  const balanceAmount = (snapshot as { balanceAmount?: unknown }).balanceAmount;
  return (
    typeof (value as { roundIdentifier?: unknown }).roundIdentifier === "string" &&
    typeof balanceAmount === "string" &&
    /^\d+$/.test(balanceAmount)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
